import { useCallback, useMemo, useState } from 'react';

import { generateShortId } from '@lyearn/core/utils/id';
import { useTranslation } from '@lyearn/i18n';
import { Trash2Fill } from '@lyearn/icons';
import { useSnackbar } from '@lyearn/ui';
import useResponse from '@/hooks/useResponse';
import { useModifyTaskCreationConfigsMutation } from '@/modules/tasks/graphql/mutations/generated/modifyTaskCreationConfigs';
import {
  ActionItemExternalEntityTypeEnum,
  CreateTaskCreationConfigInput,
  IntegrationProviderEnum,
  KeyResultType,
  ObjectiveType,
  OkrTaskCreationConfig,
  OperationEnum,
  ProviderType,
  TaskCreationConfigTypeEnum,
  TaskCreationEntityTypeEnum,
  UpdateTaskCreationConfigInput,
} from '@/types/schema';

import { useTaskCreationConfigFileQuery } from '../../../../views/IntegrationsManager/graphql/queries/generated/taskCreationConfigFile';
import { useTaskCreationConfigOkrsQuery } from '../../../../views/IntegrationsManager/graphql/queries/generated/taskCreationConfigOkrs';
import { AutoSyncFromJiraModalProps } from '../../components/AutoSyncFromJiraModal/types';
import useSubscribeToSyncConfig from '../useSubscribeToSyncConfig';

const EXTERNAL_ENTITY_TYPE_MAP: Record<string, ActionItemExternalEntityTypeEnum> = {
  [IntegrationProviderEnum.Jira]: ActionItemExternalEntityTypeEnum.JiraIssue,
};

const getInitialTaskCreationConfig = (provider: ProviderType) => ({
  providerId: provider.id,
  searchText: '',
  taskCreationType: TaskCreationConfigTypeEnum.Auto,
  id: generateShortId(),
  externalEntityType: EXTERNAL_ENTITY_TYPE_MAP[provider.integrationProvider?.provider!],
});

/**
 *
 * if props.enitityId, then it's used in OKR
 * if props.fileId, then It's used in tasks (File & Folder list)
 *
 */

export default function useSyncFromJiraModal(props: AutoSyncFromJiraModalProps) {
  const { t } = useTranslation('Jira');
  const { provider, entityId, fileId } = props;
  const [inViewJQLQueryDialog, setInViewJQLQueryDialog] = useState<
    OkrTaskCreationConfig | CreateTaskCreationConfigInput
  >();
  const { handleResponse } = useResponse();
  const { showSnackbar } = useSnackbar();
  const closeJQLQueryDialog = useCallback(() => {
    setInViewJQLQueryDialog(undefined);
  }, []);

  const [{ fetching: fetchingOkr, data: okrData, stale: okrStale }, executeOKRsTaskCreationConfig] =
    useTaskCreationConfigOkrsQuery({
      variables: {
        okrId: entityId!,
      },
      pause: !entityId,
      requestPolicy: 'cache-and-network',
    });

  const [
    { fetching: fetchingFile, data: fileData, stale: fileStale },
    executeFileTaskCreationConfig,
  ] = useTaskCreationConfigFileQuery({
    variables: {
      fileId: fileId!,
    },
    pause: !fileId || !!entityId,
  });

  const isFetching =
    fetchingOkr ||
    fetchingFile ||
    (okrStale && okrData?.okr?.taskCreationConfigs === null) ||
    (fileStale && fileData?.file?.taskCreationConfigs === null);

  const [, modifyTaskCreationConfigs] = useModifyTaskCreationConfigsMutation();

  const taskCreationConfigs = useMemo(() => {
    if (entityId) {
      return (okrData?.okr as ObjectiveType | KeyResultType | undefined)?.taskCreationConfigs as
        | OkrTaskCreationConfig[]
        | undefined;
    }

    if (fileId) {
      return fileData?.file?.taskCreationConfigs as OkrTaskCreationConfig[] | undefined;
    }

    return undefined;
  }, [entityId, fileId, okrData?.okr, fileData?.file?.taskCreationConfigs]);

  const { resetRetryCount, isRetry } = useSubscribeToSyncConfig({
    executeQuery: entityId ? executeOKRsTaskCreationConfig : executeFileTaskCreationConfig,
    taskCreationConfigs,
  });

  const onAddJQLQueryClick = useCallback(
    () => setInViewJQLQueryDialog(getInitialTaskCreationConfig(provider)),
    [provider],
  );

  const refetchOKRsTaskCreationConfig = useCallback(() => {
    if (entityId) {
      executeOKRsTaskCreationConfig();
    }
  }, [entityId, executeOKRsTaskCreationConfig]);

  const onAddJQLQuery = useCallback(
    async (createTaskCreationConfig: CreateTaskCreationConfigInput) => {
      const data = await modifyTaskCreationConfigs({
        data: {
          entityId: entityId || fileId,
          entityType: entityId
            ? TaskCreationEntityTypeEnum.Okr
            : fileId
            ? TaskCreationEntityTypeEnum.File
            : undefined,
          inputs: {
            createTaskCreationConfigInputs: [createTaskCreationConfig],
          },
          command: OperationEnum.Create,
          providerId: provider.id,
        },
      });

      handleResponse({
        condition: !!(entityId
          ? data.data?.modifyTaskCreationConfigs?.okr
          : fileId
          ? data.data?.modifyTaskCreationConfigs?.file
          : undefined),
        onSuccess: () => {
          showSnackbar({
            message: t('Jira:JQLQueryAddedSuccessfully'),
          });
          closeJQLQueryDialog();
        },
      });
      refetchOKRsTaskCreationConfig();
      resetRetryCount();
    },
    [
      modifyTaskCreationConfigs,
      entityId,
      fileId,
      provider.id,
      handleResponse,
      refetchOKRsTaskCreationConfig,
      resetRetryCount,
      showSnackbar,
      t,
      closeJQLQueryDialog,
    ],
  );

  const onUpdateJQLQuery = useCallback(
    async (updateTaskCreationConfig: UpdateTaskCreationConfigInput) => {
      const data = await modifyTaskCreationConfigs({
        data: {
          entityId: entityId || fileId,
          entityType: entityId
            ? TaskCreationEntityTypeEnum.Okr
            : fileId
            ? TaskCreationEntityTypeEnum.File
            : undefined,
          inputs: {
            updateTaskCreationConfigInputs: [updateTaskCreationConfig],
          },
          command: OperationEnum.Update,
          providerId: provider.id,
        },
      });

      handleResponse({
        condition: !!(entityId
          ? data.data?.modifyTaskCreationConfigs?.okr
          : fileId
          ? data.data?.modifyTaskCreationConfigs?.file
          : undefined),
        onSuccess: () => {
          showSnackbar({
            message: t('Jira:JQLQueryUpdatedSuccessfully'),
          });
          closeJQLQueryDialog();
        },
      });
      refetchOKRsTaskCreationConfig();
    },
    [
      modifyTaskCreationConfigs,
      entityId,
      fileId,
      provider.id,
      handleResponse,
      refetchOKRsTaskCreationConfig,
      showSnackbar,
      t,
      closeJQLQueryDialog,
    ],
  );

  const onDeleteJQLQuery = useCallback(
    async (taskCreationConfigId: string) => {
      const data = await modifyTaskCreationConfigs({
        data: {
          entityId: entityId || fileId,
          entityType: entityId
            ? TaskCreationEntityTypeEnum.Okr
            : fileId
            ? TaskCreationEntityTypeEnum.File
            : undefined,
          inputs: {
            removeTaskCreationConfigInputs: [taskCreationConfigId],
          },
          command: OperationEnum.Remove,
          providerId: provider.id,
        },
      });

      handleResponse({
        condition: !!(entityId
          ? data.data?.modifyTaskCreationConfigs?.okr
          : fileId
          ? data.data?.modifyTaskCreationConfigs?.file
          : undefined),
        onSuccess: () =>
          showSnackbar({
            message: t('Jira:JQLQueryDeleted'),
            startIcon: <Trash2Fill fillColorToken="icon-placeholder" />,
          }),
      });
      refetchOKRsTaskCreationConfig();
    },
    [
      entityId,
      fileId,
      handleResponse,
      modifyTaskCreationConfigs,
      provider.id,
      refetchOKRsTaskCreationConfig,
      showSnackbar,
      t,
    ],
  );

  const onAddJQLQueryModalSubmit = useCallback(
    (searchText: string) => {
      if (!inViewJQLQueryDialog?.searchText) {
        onAddJQLQuery({
          searchText,
          providerId: provider.id,
          taskCreationType: TaskCreationConfigTypeEnum.Auto,
          externalEntityType: EXTERNAL_ENTITY_TYPE_MAP[provider.integrationProvider?.provider!],
        });
        return;
      }

      onUpdateJQLQuery({
        searchText,
        taskCreationType: TaskCreationConfigTypeEnum.Auto,
        taskCreationConfigId: (inViewJQLQueryDialog as OkrTaskCreationConfig).id,
        providerId: provider.id,
      });
    },
    [
      inViewJQLQueryDialog,
      onAddJQLQuery,
      onUpdateJQLQuery,
      provider.id,
      provider.integrationProvider?.provider,
    ],
  );

  return {
    state: {
      taskCreationConfigs,
      inViewJQLQueryDialog,
      fetching: isFetching && !isRetry,
    },
    methods: {
      closeJQLQueryDialog,
      onAddJQLQueryClick,
      setInViewJQLQueryDialog,
      onAddJQLQueryModalSubmit,
      onDeleteJQLQuery,
    },
  };
}
